import { localeRegex } from "@app/lib/schemas/locale";
import z, { ZodError } from "zod";

const exporterMap = {
	csv: "csv",
	excel: "excel",
	pdf: "pdf",
};
const viewSchema = z
	.enum([
		"grouped_by_project",
		"grouped_by_client",
		"grouped_by_user",
		"grouped_by_workspace",
		"project_summary",
		"project_summary_user",
		"simple_list",
		"project_budget",
	])
	.default("grouped_by_project");
const projectSummaryUserMetricSchema = z.enum(["billable_hours", "income", "all_times", "non_billable_hours"]);
export const exportProjectHoursSchema = z
	.object({
		users: z.union([z.uuid().array().min(1), z.enum(["all", "enabled", "all_with_removed", "current_user"])]),
		tasks: z.uuid().array().nullish(),
		clients: z.uuid().array().nullish(),
		tags: z.uuid().array().nullish(),
		projectTags: z.uuid().array().nullish(),
		billable: z.enum(["billable", "not_billable", "both"]).nullish(),
		completed: z.enum(["completed", "not_completed", "both"]).nullish(),
		projects: z.union([z.uuid().array().min(1), z.enum(["all", "all_with_archived", "archived", "member_of"])]),
		// keep: keep the time zone of the time entry
		// local: use the local time zone of the user (ctx.user.settings.timeZone)
		timezone: z
			.enum(["keep", "local"])
			.nullish()
			.transform((v) => v ?? "keep"),
		range: z.union([
			z.object({
				start: z.coerce.date(),
				end: z.coerce.date(),
			}),
			z.coerce.date(),
			z.enum([
				"today",
				"yesterday",
				"this_week",
				"last_week",
				"this_month",
				"last_month",
				"this_year",
				"last_year",
				"all_time",
			]),
		]),
		type: z
			.enum(["csv", "excel", "pdf"])
			.refine((type) => Object.keys(exporterMap).includes(type), {
				message: "Invalid or temporarily disabled exporter type",
			})
			.default("csv"),
		view: viewSchema,
		locale: z
			.string()
			.refine((locale) => localeRegex.test(locale), {
				message: "Invalid locale",
			})
			.nullish()
			.default("de-DE"),
		ampm: z.boolean().nullish().default(false),
		rounding: z
			.object({
				minutes: z.number().min(0).max(60),
				type: z.enum(["upto", "nearest", "downto"]),
			})
			.nullish(),
		columns: z
			.array(z.string())
			.nullish()
			.transform((v) => (v?.length ? v : null)),
		projectSummary: z.object({
			metric: z.enum(["billable_hours", "income", "all_times", "non_billable_hours"]),
			includeCompleted: z.boolean(),
			budgetFilter: z.enum(["all", "with_budget"]),
		}),
		projectSummaryUser: z.object({
			metric: z
				.enum(["billable_hours", "income", "all_times", "non_billable_hours"])
				.nullish()
				.transform((v) => v ?? "billable_hours"),
			projectAlias: z
				.enum(["name", "externalId", "id"])
				.nullish()
				.transform((v) => v ?? "name"),
		}),
	})
	.refine((data) => {
		const viewByType: Record<keyof typeof exporterMap, z.infer<typeof viewSchema>[]> = {
			excel: [
				"grouped_by_project",
				"grouped_by_user",
				"grouped_by_workspace",
				"project_summary",
				"project_summary_user",
			],
			csv: ["simple_list", "project_summary", "project_summary_user"],
			pdf: ["grouped_by_client"],
		};

		if (viewByType[data.type] && !viewByType[data.type].includes(data.view)) {
			return new ZodError([
				{
					code: "custom",
					message: `View "${data.view}" is not allowed for export type "${data.type}"`,
					path: ["view"],
				},
			]);
		}
		return true;
	});
export type ExportProjectHoursSchema = z.infer<typeof exportProjectHoursSchema>;
